import { SettlementStatusSchema } from '@neon-one/auctions-types';
import type { EventMoney } from '@neon-one/auctions-types';

import { InvoiceStatus, invoiceDisplayStatus, isDeclinedInvoice, moneyCollected, netPaidCents } from './payment-status';
import type { PaymentInvoice } from './types';

const STATUS = SettlementStatusSchema.enum;

export type ConsoleMoney = Pick<
  EventMoney,
  | 'collectedCents'
  | 'clearingCents'
  | 'unconfirmedCents'
  | 'outstandingCents'
  | 'expectedRevenueCents'
  | 'biddersOwingCount'
  | 'biddersSettledCount'
  | 'declinedInvoicesCount'
>;

interface BidderTally {
  dueCents: number;
  // A failed refund, unconfirmed payment or claimed charge is not done,
  // even when no collectible balance remains.
  needsAction: boolean;
}

function tallyBidder(byBidder: Map<string, BidderTally>, invoice: PaymentInvoice, dueCents: number): void {
  const tally = byBidder.get(invoice.bidderUuid) ?? { dueCents: 0, needsAction: false };
  tally.dueCents += dueCents;
  const display = invoiceDisplayStatus(invoice);
  tally.needsAction ||= display === InvoiceStatus.RefundFailed || display === InvoiceStatus.Unconfirmed
    || invoice.status === STATUS.processing;
  byBidder.set(invoice.bidderUuid, tally);
}

function bidderTallies(invoices: readonly PaymentInvoice[]): Map<string, BidderTally> {
  const byBidder = new Map<string, BidderTally>();
  for (const invoice of invoices) {
    if (invoice.status === STATUS.void) continue;
    tallyBidder(byBidder, invoice, moneyCollected(invoice.status) ? 0 : netPaidCents(invoice));
  }
  return byBidder;
}

const bidderIsSettled = (tally: BidderTally): boolean => tally.dueCents === 0 && !tally.needsAction;

/** Bidders with nothing left to do across every non-void event invoice. */
export function settledBidderUuids(invoices: readonly PaymentInvoice[]): ReadonlySet<string> {
  return new Set([...bidderTallies(invoices)]
    .filter(([, tally]) => bidderIsSettled(tally))
    .map(([uuid]) => uuid));
}

/** The same buckets the server sums, read off the roster. Void invoices hold nothing. */
export function consoleMoneyFromRoster(invoices: readonly PaymentInvoice[]): ConsoleMoney {
  const money: ConsoleMoney = {
    collectedCents: 0,
    clearingCents: 0,
    unconfirmedCents: 0,
    outstandingCents: 0,
    expectedRevenueCents: 0,
    biddersOwingCount: 0,
    biddersSettledCount: 0,
    declinedInvoicesCount: 0,
  };
  for (const invoice of invoices) {
    if (invoice.status === STATUS.void) continue;
    // Net of every refund that settled, is settling, or failed: refunded money
    // is never still collected, and money owed back is nobody's revenue.
    const keptCents = netPaidCents(invoice);
    const collected = moneyCollected(invoice.status);
    if (collected) money.collectedCents += keptCents;
    else money.outstandingCents += keptCents;
    if (invoice.status === STATUS.clearing) money.clearingCents += keptCents;
    if (invoice.status === STATUS.unconfirmed) money.unconfirmedCents += keptCents;
    if (isDeclinedInvoice(invoice)) money.declinedInvoicesCount += 1;
  }
  money.expectedRevenueCents = money.collectedCents + money.outstandingCents;
  for (const tally of bidderTallies(invoices).values()) {
    if (tally.dueCents > 0) money.biddersOwingCount += 1;
    else if (bidderIsSettled(tally)) money.biddersSettledCount += 1;
  }
  return money;
}
